// src/pages/MyDocumentsPage.jsx

import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiFileText, FiCheckCircle, FiClock, FiAlertCircle, FiZap, FiLoader } from 'react-icons/fi';
import { supabase } from '../lib/supabaseClient';
import PDLDocumentsTable from '../components/documents/PDLDocumentsTable';

// COMPOSANT PRINCIPAL MyDocumentsPage
const MyDocumentsPage = ({ 
    pdls, 
    userProfile, 
    documents,
    onDataChange
}) => {
  // Écouteur temps-réel pour notifier le parent de rafraîchir
  useEffect(() => {
    if (!userProfile) return;
    const channel = supabase
      .channel(`realtime-docs-${userProfile.id}`)
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'user_uploaded_documents', 
        filter: `user_id=eq.${userProfile.id}` 
      }, (payload) => {
        if (onDataChange) {
            onDataChange();
        }
      })
      .subscribe();

    return () => { 
      supabase.removeChannel(channel); 
    };
  }, [userProfile, onDataChange]);

  // Si les données de base ne sont pas encore arrivées, on affiche un loader
  if (!pdls) { 
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FiLoader className="animate-spin mx-auto h-12 w-12 text-blue-600 mb-4" />
          <p className="text-gray-600">Chargement des points de livraison...</p>
        </div>
      </div>
    ); 
  }

  // Calcul des statistiques globales (c'est le bon endroit pour ça)
  const documentsStats = useMemo(() => {
    // ✅ Utilisez les documents passés en props plutôt que ceux des PDL
    const allUploadedFiles = documents || [];
    return {
        total: allUploadedFiles.length,
        validated: allUploadedFiles.filter(file => file.status === 'Validé').length,
        pending: allUploadedFiles.filter(file => ['En attente', 'Fourni'].includes(file.status)).length,
        rejected: allUploadedFiles.filter(file => file.status === 'Rejeté').length,
    };
  }, [documents]); // ✅ Dépendance sur documents au lieu de pdls

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Mes Documents</h1>
              <p className="mt-2 text-sm text-gray-600">
                Suivi de la collecte de documents pour chacun de vos points de livraison.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Documents Fournis</h3>
                <p className="text-2xl font-bold text-gray-900">{documentsStats.total}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full"><FiFileText className="w-6 h-6 text-blue-600" /></div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Validés</h3>
                <p className="text-2xl font-bold text-gray-900">{documentsStats.validated}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full"><FiCheckCircle className="w-6 h-6 text-green-600" /></div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-500">En Attente</h3>
                <p className="text-2xl font-bold text-gray-900">{documentsStats.pending}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full"><FiClock className="w-6 h-6 text-orange-600" /></div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Rejetés</h3>
                <p className="text-2xl font-bold text-gray-900">{documentsStats.rejected}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full"><FiAlertCircle className="w-6 h-6 text-red-600" /></div>
            </div>
          </div>
        </div>
        
        {pdls && pdls.length > 0 ? (
          <PDLDocumentsTable
            pdls={pdls}
            onRefresh={onDataChange}
          />
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <FiZap className="mx-auto h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900">Aucun Point de Livraison trouvé</h3>
            <p className="text-gray-600 mt-2">
              Veuillez d'abord <Link to="/dashboard/pdl" className="text-blue-600 font-semibold hover:underline">ajouter un PDL</Link>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyDocumentsPage;